import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useExperimentApi } from '../../../../../store/api/hooks/experimentApiHook'

interface SavedFilter {
    sql_query?: string | null
    sql?: string | null
    filters?: unknown
}

interface FilterPreviewPayload {
    sql_query?: string
    filters?: unknown
}

interface ChatFilterRestoreOptions {
    literal?: string
    filterKey: string
    previewRequestRef: MutableRefObject<number>
    getExperimentDetails: ReturnType<typeof useExperimentApi>['getFilterRestoreDetails']
    preview: (payload: FilterPreviewPayload, onSettled: (success: boolean) => void) => void
    setFilters: (payload: FilterPreviewPayload | null) => void
    // Releases the loader this host showed for an in-flight preview; called when that preview is canceled
    // (reset or unmount) rather than settling normally, since a dropped response never calls its own onSettled.
    onCancel?: () => void
}

export const useChatFilterRestore = ({ literal, filterKey, previewRequestRef, getExperimentDetails, preview, setFilters, onCancel }: ChatFilterRestoreOptions) => {
    const [status, setStatus] = useState<'ready' | 'loading' | 'failed'>('ready')
    const [savedFilter, setSavedFilter] = useState<SavedFilter | null>(null)
    // Whether this host's preview() call is currently showing a loader it alone owns.
    const previewInFlightRef = useRef(false)
    const onCancelRef = useRef(onCancel)
    onCancelRef.current = onCancel

    const cancelInFlightPreview = useCallback(() => {
        previewRequestRef.current += 1
        if (previewInFlightRef.current) {
            previewInFlightRef.current = false
            onCancelRef.current?.()
        }
    }, [previewRequestRef])

    const reset = useCallback(() => {
        cancelInFlightPreview()
        setStatus('ready')
        setSavedFilter(null)
    }, [cancelInFlightPreview])

    // Cancel (and release the loader for) an in-flight preview so a callback that resolves after unmount is ignored.
    useEffect(() => {
        return () => cancelInFlightPreview()
    }, [cancelInFlightPreview])

    const restore = () => {
        setStatus('loading')
        const requestId = ++previewRequestRef.current
        getExperimentDetails(
            { literal: literal + '?use_orm=true' },
            (current: any) => {
                if (requestId !== previewRequestRef.current) return
                const saved: SavedFilter = current?.experiment_metadatas?.[filterKey] ?? {}
                const sql = saved.sql_query ?? saved.sql
                const payload: FilterPreviewPayload = sql ? { sql_query: sql } : {}
                if (!sql && saved.filters) payload.filters = saved.filters
                setSavedFilter(saved)
                setFilters(saved.filters && !sql ? payload : null)
                previewInFlightRef.current = true
                preview(payload, (success) => {
                    previewInFlightRef.current = false
                    if (previewRequestRef.current === requestId + 1) setStatus(success ? 'ready' : 'failed')
                })
            },
            (error: any) => {
                if (requestId !== previewRequestRef.current) return
                setStatus('failed')
                toast.error(error?.message || 'Failed to fetch experiment details')
            },
        )
    }

    return { status, savedFilter, reset, restore }
}
